import { useEffect, useRef, useState, Fragment } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import UserAvatarMenu from "../useravatarmenu/UserAvatarMenu";
import { useLiveStatus } from "../../context/LiveStatusContext";
import "./mobileMenu.css";

/* ── Mobile-drawer icon set: 24×24, currentColor, thin line ───────────────── */
const MmIc = ({ children, size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);
const MmIconLive = () => (
  <MmIc>
    <circle cx="12" cy="12" r="2.1" />
    <path d="M8.1 8.1a5.5 5.5 0 0 0 0 7.8M15.9 8.1a5.5 5.5 0 0 1 0 7.8" />
    <path d="M5.4 5.4a9.3 9.3 0 0 0 0 13.2M18.6 5.4a9.3 9.3 0 0 1 0 13.2" />
  </MmIc>
);
const MmIconSettings = () => (
  <MmIc>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2.6v2.3M12 19.1v2.3M21.4 12h-2.3M4.9 12H2.6M18.6 5.4l-1.6 1.6M7 17l-1.6 1.6M18.6 18.6 17 17M7 7 5.4 5.4" />
  </MmIc>
);
const MmIconLectures = () => (
  <MmIc>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M6 11.5a6 6 0 0 0 12 0M12 17.5V21M9 21h6" />
  </MmIc>
);
const MmIconVideo = () => (
  <MmIc>
    <rect x="3" y="6" width="13" height="12" rx="3" />
    <path d="M16 10.5 21 8v8l-5-2.5z" />
  </MmIc>
);
const MmIconLibrary = () => (
  <MmIc>
    <path d="M5 4.5h9a2.5 2.5 0 0 1 2.5 2.5v12.5H7.5A2.5 2.5 0 0 1 5 19V4.5Z" />
    <path d="M16.5 7H18a1.5 1.5 0 0 1 1.5 1.5V19a2.5 2.5 0 0 0-2.5-2.5h-1.5" />
    <path d="M5 16.5h11.5" />
  </MmIc>
);
const MmIconPlaylists = () => (
  <MmIc>
    <path d="M4 6h12M4 11h12M4 16h7M18 13v7M14.5 16.5h7" />
  </MmIc>
);
const MmIconAdmin = () => (
  <MmIc>
    <path d="M12 3 5 6v5.5c0 4.3 2.9 7.6 7 9 4.1-1.4 7-4.7 7-9V6l-7-3Z" />
    <path d="m9.3 11.8 1.9 1.9 3.6-3.8" />
  </MmIc>
);
const MmIconChevron = ({ open }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const MmIconArrow = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);
const MmIconClose = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);
const MmIconPower = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 4v8" />
    <path d="M7.5 7a7 7 0 1 0 9 0" />
  </svg>
);

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const { isLive } = useLiveStatus();
  const isAdmin =
    user?.role === "admin" ||
    user?.role === "superadmin" ||
    user?.roles?.includes?.("admin") ||
    user?.isAdmin === true;
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerElRef = useRef(null);

  // The header is position: fixed, so pages underneath have to know how tall it
  // actually is. Publishing the measured height means no page has to hardcode a
  // guess — which is how the back button on the lecture page ended up hidden
  // behind it. getBoundingClientRect keeps the fractional height that
  // offsetHeight rounds away; rounding up avoids a sub-pixel gap.
  useEffect(() => {
    const el = headerElRef.current;
    if (!el) return;

    const apply = () =>
      document.documentElement.style.setProperty(
        "--app-header-h",
        `${Math.ceil(el.getBoundingClientRect().height)}px`
      );

    apply();

    // Write on the next frame: changing layout inside the observer callback
    // makes the browser report "ResizeObserver loop completed with
    // undelivered notifications" (e.g. when a modal locks body scroll and the
    // scrollbar disappears), which CRA's dev overlay shows as a crash.
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);
  // Which mobile-drawer submenus are expanded (Lectures / Programs).
  const [openSub, setOpenSub] = useState({});
  const toggleSub = (id) =>
    setOpenSub((o) => ({ ...o, [id]: !o[id] }));

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const detailsRef = useRef(null);

  const forceBlackHeaderRoutes = [
    "/live",
    "/settings",
    "/profile",
    "/video",
    "/video-lectures",
    "/audio",
    "/audio-lectures",
    "/annual-events",
    "/past-streams",
    "/mehfil",
    "/library",
    "/playlists",
  ];
  const forceBlackHeader =forceBlackHeaderRoutes.some((route) =>
    pathname.startsWith(route),
  );

  const forceBlackHeaderRoutesMobile = ["/live", "/settings", "/profile"];
  const forceBlackHeaderMobile = forceBlackHeaderRoutes.includes(pathname);

  const isLogin = pathname === "/login";
  const isSignup = pathname === "/signup";

  // Header background on scroll (desktop focus)
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [menuOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (detailsRef.current && !detailsRef.current.contains(event.target)) {
        detailsRef.current.removeAttribute("open");
      }
    }

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);
  // Auto-close mobile drawer on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Simple desktop navigation helper
  const handleNavigation = (path) => {
    navigate(path);
  };

  const getInitials = (name = "", email = "") => {
    const n = name.trim();
    if (n)
      return n
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0])
        .join("")
        .toUpperCase();
    const local = (email || "").split("@")[0] || "";
    return local ? local[0].toUpperCase() : "?";
  };

  // ── Mobile drawer nav config (order: Admin, Live, Lectures, Library,
  //    Settings — Admin only for admins) ──────────────────────────────────
  const isRouteActive = (to) =>
    !!to && (pathname === to || pathname.startsWith(to + "/"));

  const mobileNav = [
    isAdmin && {
      id: "admin",
      label: "Admin",
      to: "/admin",
      Icon: MmIconAdmin,
    },
    { id: "live", label: "Live", to: "/live", Icon: MmIconLive, live: true },
    { id: "audio", label: "Audio", to: "/audio", Icon: MmIconLectures },
    { id: "video", label: "Video", to: "/video", Icon: MmIconVideo },
    { id: "library", label: "Library", to: "/library", Icon: MmIconLibrary },
    { id: "playlists", label: "Playlists", to: "/playlists", Icon: MmIconPlaylists },
    { id: "settings", label: "Settings", to: "/settings", Icon: MmIconSettings },
  ].filter(Boolean);

  const goTo = (to) => {
    setMenuOpen(false);
    navigate(to);
  };

  const Brand = (
    <div className="flex items-start gap-2 flex-1 min-w-0">
      <img
        src="/images/logoImage.png"
        alt="Logo"
        width={100}
        height={100}
        className="logoAdjust rounded-full"
      />
      <div className="leading-tight min-w-0 max-w-[60vw] md:max-w-none">
        <div className="text-[10px] md:text-lg font-arabic text-white whitespace-nowrap">
          خانقةاللّٰہِ
        </div>
        <div className="text-[11px] md:text-xl md:tracking-wide text-white truncate">
          KHANQATULLAH
        </div>
      </div>
    </div>
  );

  return (
    <>
      <header
        ref={headerElRef}
        className={`headerFixed fixed top-0 left-0 right-0
    w-full flex items-start justify-between px-6 md:px-16
    z-[200] pointer-events-auto transition-all duration-300
    ${
      !isAuthenticated || forceBlackHeader
        ? "bg-black"
        : scrolled
          ? "bg-black/70 shadow-lg backdrop-blur-md"
          : "bg-transparent"
    }
        h-14 md:h-24`}
      >
        {/* Brand: non-interactive on mobile to avoid stealing taps; clickable on desktop */}
        {isAuthenticated ? (
          <Link to="/home" className="absolute z-20 pointer-events-auto">
            {Brand}
          </Link>
        ) : (
          <div className="absolute z-20 pointer-events-auto">{Brand}</div>
        )}

        {/* RIGHT SIDE (desktop nav + auth buttons) */}
        <div className="flex items-center gap-10 z-50 ml-auto">
          {/* Desktop Navigation (authed) */}
          {isAuthenticated && (
            <nav className="hidden md:flex items-center gap-14 text-sm font-medium relative">
              {/* Streaming */}
              <Link
                to="/live"
                className="hover:text-gray-400 transition flex items-center gap-1 no-underline text-white"
              >
                {isLive && (
                  <span className="animate-pulse">
                    <img
                      src="/images/liveImage.png"
                      alt="Live"
                      className="h-10 w-10"
                    />
                  </span>
                )}
                <span className="text-[14px] md:text-[20px]">Live Stream</span>
              </Link>
              {/* Audio */}
              <Link
                to="/audio"
                className="hover:text-gray-400 transition no-underline text-white text-[14px] md:text-[20px]"
              >
                Audio
              </Link>

              {/* Video */}
              <Link
                to="/video"
                className="hover:text-gray-400 transition no-underline text-white text-[14px] md:text-[20px]"
              >
                Video
              </Link>

              {/* Wazaif */}
              <Link
                to="/library"
                className="hover:text-gray-400 transition no-underline text-white text-[14px] md:text-[20px]"
              >
                Library
              </Link>

              {/* Playlists */}
              <Link
                to="/playlists"
                className="hover:text-gray-400 transition no-underline text-white text-[14px] md:text-[20px]"
              >
                Playlists
              </Link>
            </nav>
          )}

          {/* Desktop: User Avatar */}
          {isAuthenticated && user?.name && (
            <div className="hidden md:flex">
              <UserAvatarMenu />
            </div>
          )}

          {/* Desktop: Auth buttons (not authed) */}
          {!isAuthenticated && (
            <div className="items-center md:mt-1 space-x-5 hidden md:flex">
              {isLogin && (
                <span className="hideforMobile text-base text-gray-300 font-[GaretRegular]">
                  Toggle to sign up
                </span>
              )}
              {isSignup && (
                <span className="hideforMobile text-base text-gray-300 font-[GaretRegular]">
                  Toggle to sign in
                </span>
              )}
              <div className="relative flex rounded-md overflow-hidden transition-all duration-300 ease-in-out">
                <button
                  onClick={() => handleNavigation("/signup")}
                  className={`px-8 py-2 cursor-pointer text-base font-semibold transition-all duration-300 ease-in-out font-[GaretRegular] ${
                    isSignup
                      ? "bg-[#f27f22] text-black z-20 rounded-md"
                      : "bg-[#181818] text-gray-400 z-10 rounded-l-md hover:bg-[#2a2a2a]"
                  }`}
                >
                  Sign Up
                </button>
                <button
                  onClick={() => handleNavigation("/login")}
                  className={`signInButtonDesktop px-8 py-2 cursor-pointer text-base font-semibold transition-all duration-300 ease-in-out font-[GaretRegular] ${
                    isLogin
                      ? "bg-[#f27f22] text-black z-20 rounded-md"
                      : "bg-[#181818] text-gray-400 z-10 rounded-r-md hover:bg-[#2a2a2a]"
                  }`}
                >
                  Sign In
                </button>
              </div>
            </div>
          )}

          {/* MOBILE TOP BAR — authed = hamburger, unauth = Sign In/Up (smaller on mobile) */}
          <div className="md:hidden flex items-center gap-1.5 z-50">
            {isAuthenticated ? (
              <button
                className="marginSet text-white p-2.5 rounded-lg hover:bg-white/10 active:scale-95 transition"
                onClick={() => setMenuOpen(true)}
                aria-label="Open menu"
                aria-expanded={menuOpen}
              >
                <svg
                  className="w-7 h-7"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    d="M4 6h16M4 12h16M4 18h16"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : (
              <div className="flex items-center">
                <Link
                  to="/signup"
                  className={`signUpButtonMobile no-underline font-[GaretRegular] text-[13px] leading-none pl-4 pr-4 py-[9px] rounded-[6px] transition
          ${
            isSignup
              ? "bg-[#f27f22] text-black"
              : "bg-[#181818] text-gray-400 hover:bg-[#2a2a2a]"
          }
        `}
                >
                  Sign Up
                </Link>
                <Link
                  to="/login"
                  className={`signInButtonMobile no-underline font-[GaretRegular] text-[13px] leading-none pl-2 px-3 py-[9px] rounded-[6px] transition
          ${
            isLogin
              ? "bg-[#f27f22] text-black"
              : "bg-[#181818] text-gray-400 hover:bg-[#2a2a2a]"
          }
        `}
                >
                  Sign In
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER OVERLAY (only when authed) — dark "ink" design */}
      {isAuthenticated && (
        <div className={`mm-overlay ${menuOpen ? "open" : ""}`}>
          <img className="mm-watermark" src="/images/logoImage.png" alt="" />

          {/* header */}
          <div className="mm-header">
            <Link
              to="/home"
              className="mm-brand"
              onClick={() => setMenuOpen(false)}
            >
              <img
                className="mm-emblem"
                src="/images/logoImage.png"
                alt="Khanqatullah emblem"
              />
              <span className="mm-word">
                <span className="mm-word-ar">خانقاة الله</span>
                <span className="mm-word-en">KHANQATULLAH</span>
              </span>
            </Link>
            <button
              className="mm-close"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
            >
              <MmIconClose />
            </button>
          </div>

          {/* scroll body */}
          <div className="mm-body">
            {user?.name && (
              <button
                type="button"
                className="mm-profile"
                onClick={() => goTo("/profile")}
              >
                {user?.picture ? (
                  <img
                    className="mm-avatar"
                    src={user.picture}
                    alt={user?.name || "User avatar"}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="mm-avatar-fallback">
                    {getInitials(user?.name, user?.email)}
                  </span>
                )}
                <span className="mm-pinfo">
                  <span className="mm-pname">{user.name}</span>
                  {user.email && <span className="mm-pmail">{user.email}</span>}
                  <span className="mm-pview">VIEW PROFILE</span>
                </span>
                <span className="mm-pchevron">
                  <MmIconArrow />
                </span>
              </button>
            )}

            <nav className="mm-nav">
              <div className="mm-nav-label">Menu</div>
              {mobileNav.map((item) => {
                const expanded = !!openSub[item.id];
                const active = item.sub
                  ? item.sub.some((s) => isRouteActive(s.to))
                  : isRouteActive(item.to);
                return (
                  <Fragment key={item.id}>
                    {item.sub ? (
                      <button
                        type="button"
                        className={`mm-item ${active ? "active" : ""}`}
                        onClick={() => toggleSub(item.id)}
                        aria-expanded={expanded}
                      >
                        <span className="mm-item-icon">
                          <item.Icon />
                        </span>
                        <span className="mm-item-label">{item.label}</span>
                        <span
                          className={`mm-item-chevron ${expanded ? "open" : ""}`}
                        >
                          <MmIconChevron />
                        </span>
                      </button>
                    ) : (
                      <Link
                        to={item.to}
                        onClick={() => setMenuOpen(false)}
                        className={`mm-item ${active ? "active" : ""}`}
                      >
                        <span className="mm-item-icon">
                          <item.Icon />
                        </span>
                        <span className="mm-item-label">{item.label}</span>
                        {item.live && isLive && (
                          <span className="mm-live-dot" />
                        )}
                      </Link>
                    )}
                    {item.sub && (
                      <div
                        className={`mm-submenu ${expanded ? "open" : ""}`}
                      >
                        <div className="mm-submenu-inner">
                          {item.sub.map((s) => (
                            <Link
                              key={s.to}
                              to={s.to}
                              onClick={() => setMenuOpen(false)}
                              className={`mm-subitem ${
                                isRouteActive(s.to) ? "active" : ""
                              }`}
                            >
                              <span className="mm-sdot" />
                              <span>{s.label}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </nav>
          </div>

          {/* footer */}
          <div className="mm-footer">
            <button
              className="mm-logout"
              onClick={() => {
                setMenuOpen(false);
                logout();
                navigate("/login");
              }}
            >
              <MmIconPower />
              <span>Log out</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
